<template>
  <div class="filial-orders-page">
    <template>
      <order-filters
        :statuses="statuses"
        :value="currentOrderStatus"
        :disabled="!!ordersSearchString"
        @filterOrders="changeStatus"
      />

      <h1
        v-if="!!ordersSearchString"
        class="display-3 font-weight-medium mb-5"
      >
        Результаты поиска:
        <!--        &laquo;-->
        <search-type-icon :search-string="ordersSearchString" class="orders-search-icon" />
        {{ ordersSearchString }}
        <!--        &raquo;-->
      </h1>
      <div class="orders-listing-area">
        <transition name="fadeUp">
          <v-loader v-if="isLoading && showBigLoader" absolute transparent />
        </transition>

        <transition
          name="fadeIn"
          :duration="{ enter: 300, leave: 0 }"
        >
          <div v-if="!showBigLoader && orders.length">
            <div
              v-for="order in orders"
              :key="order.id"
            >
              <order-card
                :order="order"
                class="order-card"
                @changeStatus="changeOrderStatus"
                @setAssemblyStatus="setAssemblyStatus"
                @setReadyStatus="setReadyStatus"
              />
            </div>
          </div>
        </transition>

        <template v-if="!isLoading && orders.length === 0">
          <span class="display-4 text-center d-block mt-16">
            <template v-if="!!ordersSearchString">
              Ничего не найдено
            </template>
            <template v-else>
              Нет заказов
            </template>
          </span>
        </template>
      </div>
    </template>

    <transition
      name="fadeUp"
      :duration="80"
    >
      <v-card
        v-if="isLoading && !showBigLoader"
        class="loading-card"
        elevation="24"
      >
        <v-loader />
      </v-card>
    </transition>
    <payment-dialog />
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import moment from 'moment'
  import VLoader from '@/components/loader/Loader'
  import OrderFilters from '@/components/order-filters/OrderFilters'
  import OrderCard from '@/components/order-card/OrderCard'
  import mixinSnackbarMessage from '@/mixins/snackbar-message'
  import _ from 'lodash'
  import axios from 'axios'
  import PaymentDialog from '@/components/order-processing/order-components/payment/PaymentDialog.vue'
  import PaymentMode from '@/models/enums/PaymentMode'
  import CRMPermission from '@/models/enums/permissions/CRMPermission'
  import PaymentStatus from '@/models/enums/PaymentStatus'
  import SearchTypeIcon from '@/components/icons/SearchTypeIcon.vue'
  import {
    DEFAULT_FILIAL_FILTER,
    buildOrdersQuery,
    getRawFilterFromRoute,
    pushOrdersRoute,
  } from '@/util/orders-route'
  import { websocketService } from '@/services/WebsocketService'
  import { refreshService } from '@/services/refresh'
  import { UPDATE_ORDER } from '@/constants/websocket-events'

  export default {
    name: 'FilialOrdersPage',
    components: {
      SearchTypeIcon,
      VLoader,
      OrderFilters,
      OrderCard,
      PaymentDialog,
    },
    mixins: [mixinSnackbarMessage],
    inject: ['announceMissedNewOrders'],
    data () {
      return {
        // Канал, на который подписались: websocketOrdersChanelName пересчитывается
        // при смене филиала, отписываться надо от старого
        ordersChannel: null,
        isLoading: false,
        showBigLoader: true,
        currentOrderStatus: getRawFilterFromRoute(this.$route) || DEFAULT_FILIAL_FILTER,
        showSearchTitle: false,
        statuses: [],
        CallCentersTabs: [],
      }
    },
    computed: {
      ...mapGetters({
        localStorageBranch: 'branches/getLocalStorageBranch',
        isCallCenter: 'branches/getGlobalFilialIsCallCenter',
        globalFilialId: 'branches/getGlobalFilialId',
      }),
      ...mapState({
        ordersSearchString: state => state.orders.ordersSearchString,
        ordersFilterDate: state => state.orders.ordersFilterDate,
        orders: state => state.orders.orders,
        currentOrderInfo: state => state.orders.currentOrderInfo,
        showPaymentDialog: state => state.orders.showPaymentDialog,
        user: state => state.auth.user || {},
        showFloatingButtons: (state) => state.app.showFloatingButtons,
        paymentMode: state => state.newOrder.paymentMode,
      }),
      authUserId () { return this.user.id || 0 },
      // диалог оплаты после оформления: только реальный неоплаченный заказ - режим «оплатить
      // сейчас» мог залипнуть в сторе от брошенного оформления, а currentOrderInfo быть чужим
      canAutoOpenPaymentDialog () {
        return this.userCan(CRMPermission.MakeOrderPayment) &&
          this.paymentMode === PaymentMode.PayNow &&
          !this.showPaymentDialog &&
          !!this.currentOrderInfo?.id &&
          this.currentOrderInfo?.payment?.status !== PaymentStatus.Success
      },
      websocketOrdersChanelName () {
        /* console.log('== websocketOrdersChanelName.canViewAllOrders ===', this.user.canViewAllOrders)
        if (this.user.canViewAllOrders) {
          return 'orders.filial.all'
        } */

        if (this.isCallCenter) {
          return 'orders.filial.call_center'
        }

        return `orders.filial.${this.localStorageBranch?.id}`
      },
    },
    watch: {
      '$route.query.filter' (value) {
        const next = value || DEFAULT_FILIAL_FILTER
        if (next === this.currentOrderStatus) {
          return
        }
        this.currentOrderStatus = next
        if (this.ordersSearchString) {
          return
        }
        this.getOrders(true)
      },
      // Значения статус-фильтра приходят с бэкенда: неизвестное значение из URL
      // откатывается к дефолту после загрузки списка, сам URL не переписывается.
      statuses (list) {
        if (!Array.isArray(list) || !list.length) {
          return
        }
        if (this.currentOrderStatus === DEFAULT_FILIAL_FILTER) {
          return
        }
        const isKnown = list.some(status => status.filterParam === this.currentOrderStatus)
        if (isKnown) {
          return
        }
        this.currentOrderStatus = DEFAULT_FILIAL_FILTER
        if (!this.ordersSearchString) {
          this.getOrders(true)
        }
      },
      ordersFilterDate () {
        this.getOrders()
        this.getFilters()
      },
      ordersSearchString () {
        if (this.ordersSearchString) {
          this.searchOrders()
        } else {
          this.getOrders(true)
        }
      },
      currentOrderInfo () {
        if (this.canAutoOpenPaymentDialog) {
          this.$store.commit('orders/setCurrentEditOrder', this.currentOrderInfo)
          this.$store.commit('orders/setShowPaymentDialog', true)
        }
      },
      isLoading (val) {
        if (!val) {
          this.showBigLoader = false
        }
      },
      /* showPaymentDialog (val) {
        if (val) {
          this.$store.commit('orders/setShowPaymentDialog', false)
        }
      }, */
    },
    created () {
      this.$store.commit('orders/setOrdersList', [])

      this.$nextTick(() => {
        if (this.canAutoOpenPaymentDialog) {
          this.$store.commit('orders/setCurrentEditOrder', this.currentOrderInfo)
          this.$store.commit('orders/setShowPaymentDialog', true)
        }

        if (this.ordersSearchString) {
          this.searchOrders()
        } else {
          this.getOrders(true)
        }
        this.getFilters()

        /* eslint-disable */
        const processCallEvent = () => {
          if (this.ordersSearchString) return
          //this.getOrders()
          this.getFilters()
        }

        this.$store.dispatch('auth/fetchUser')
          .then(() => {
            // Сокет общий на вкладку: подписка ушедшей страницы дёргала бы
            // getOrders уничтоженного компонента
            if (this._isDestroyed) return
            this.ordersChannel = this.websocketOrdersChanelName
            websocketService.listenEvent(this.ordersChannel, UPDATE_ORDER, this.processWebsocketEvent)
          })
        refreshService.register(this.refreshOrders, { probe: this.ordersProbe })
      })
    },
    beforeDestroy () {
      this.$store.commit('orders/setOrdersSearchString', null)
      // Только со своим колбэком: App.vue слушает тот же UpdateOrder на том же
      // канале ради звука нового заказа
      if (this.ordersChannel) {
        websocketService.stopListening(this.ordersChannel, UPDATE_ORDER, this.processWebsocketEvent)
      }
      refreshService.unregister(this.refreshOrders)

      /*  echo.channel('orders.filial.all')
         .stopListening('Order\\UpdateOrder')
         // .stopListening('Order\\UpdateStatusesOrder')
       this.$store.commit('orders/clearOrdersList') */

    },
    methods: {
      changeStatus (value) {
        pushOrdersRoute(this.$router, buildOrdersQuery({
          filter: value === DEFAULT_FILIAL_FILTER ? null : value,
          search: this.ordersSearchString,
        }))
      },
      setAssemblyStatus (id) {
        const data = {
          orderStatus: 'assembly',
        }
        this.$store.dispatch('orders/updateOrder', { id, data })
          .then(res => {
            if (res.status === 200) {
              this.getOrders()
            } else {
              this.showErrorMessage(res.response.data.message)
            }
          })
      },
      setReadyStatus (id) {
        const data = {
          orderStatus: 'readyForIssue',
        }
        this.$store.dispatch('orders/updateOrder', { id, data })
          .then(res => {
            if (res.status === 200) {
              this.getOrders()
            } else {
              this.showErrorMessage(res.response.data.message)
            }
          })
      },
      changeOrderStatus (id) {
        this.$store.dispatch('orders/changeOnNextStatus', id)
          .catch(e => this.showErrorFromResponse(e))
      },
      getOrders (showBigLoader = false, { silent = false } = {}) {
        if (!this.globalFilialId) {
          return
        }
        this.isLoading = true
        this.showBigLoader = showBigLoader
        const payload = {
          limit: 10000,
          offset: 0,
          page: 1,
          filterParam: this.currentOrderStatus,
          date: this.ordersFilterDate,
          filialId: this.globalFilialId
        }
        return this.$store
          .dispatch('orders/fetchOrdersList', payload)
          .then((res) => {
            this.isLoading = false
            this.showSearchTitle = false
            this.rememberOrdersVersion(res)
          })
          .catch((err) => {
            if (!silent) {
              this.showErrorFromResponse(err)
            }
            if (!axios.isCancel(err)) {
              this.isLoading = false
              this.showSearchTitle = false
            }
          })
      },
      searchOrders () {
        this.isLoading = true
        this.showBigLoader = true
        const payload = {
          limit: 30,
          offset: 0,
          page: 1,
          date: moment(new Date(Date.now())).format('YYYY-MM-DD'),
          globalSearch: this.ordersSearchString,
        }
        this.$store
          .dispatch('orders/fetchOrdersList', payload)
          .then(() => {
            this.isLoading = false
            this.showSearchTitle = true
          })
          .catch((err) => {
            this.showErrorFromResponse(err)
            if (!axios.isCancel(err)) {
              this.isLoading = false
              this.showSearchTitle = false
            }
          })
      },
      getFilters ({ silent = false } = {}) {
        if (!this.globalFilialId) {
          return
        }
        const payload = {
          date: this.ordersFilterDate,
          filialId: this.globalFilialId,
        }
        return this.$store.dispatch('orders/fetchStatusesList', payload)
          .then(res => {
            this.statuses = res
          })
          .catch((err) => {
            if (!silent) {
              this.showErrorFromResponse(err)
            }
          })
      },
      // Параметры версии для сторожа: те же, что у getOrders. В режиме поиска на экране
      // результат поиска - версию списка не проверяем
      ordersProbe () {
        if (this.ordersSearchString || !this.globalFilialId) return null
        return {
          filialId: this.globalFilialId,
          date: this.ordersFilterDate,
          filterParam: this.currentOrderStatus,
        }
      },
      // Версия только что загруженного списка: сторож сравнит с ней и не станет
      // перечитывать то, что мы сами только что получили
      rememberOrdersVersion (res) {
        const meta = res && res.data && res.data.meta
        refreshService.setVersion(this.refreshOrders, (meta && meta.version) || null)
      },
      // Догоняем пропущенное после разрыва сокета и по сторожу. В режиме поиска
      // на экране результат поиска - его не перезаписываем, как processWebsocketEvent
      refreshOrders ({ silent }) {
        if (this.ordersSearchString) return
        const shownOrders = this.orders
        const shownFilter = this.currentOrderStatus
        const shownDate = this.ordersFilterDate
        return Promise.all([this.getOrders(false, { silent }), this.getFilters({ silent })])
          .then(() => {
            // Пока шёл запрос, сменили фильтр, дату или начали поиск - на экране другой
            // список, и новые в нём заказы не пропущенные
            const sameView = !this.ordersSearchString &&
              this.currentOrderStatus === shownFilter &&
              this.ordersFilterDate === shownDate
            if (!sameView) return
            this.announceMissedNewOrders(shownOrders, this.orders)
          })
      },
      processWebsocketEvent: _.debounce(function () {
        if (this.ordersSearchString) return
        this.getOrders()
        this.getFilters()
      }, 1000),
    },
  }
</script>

<style lang="less" scoped>
.filial-orders-page {

  .orders-search-icon {
    color: @color-primary;
    margin-right: 3px;
    margin-left: 5px;
  }

  .orders-listing-area {
    min-height: 200px;
    position: relative;
  }
}
.order-card {
  margin-bottom: 10px;
}
.loading-card {
  z-index: 5;
  position: fixed;
  right: 24px;
  bottom: 24px;
  ::v-deep {
    .loader-component div {
      margin: 20px;
    }
  }
}
</style>

